import { Link } from 'react-router-dom';
import type { Product } from '@/mocks/products';
import { useCompare } from '@/context/CompareContext';
import { useTheme } from '@/context/ThemeContext';
import { useCampaign } from '@/context/CampaignContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

interface Props {
  product: Product;
}

export default function ProductCard({ product }: Props) {
  const { toggle, isSelected, canAdd } = useCompare();
  const { theme } = useTheme();
  const { getDiscountedPrice } = useCampaign();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';
  const selected = isSelected(product.id);
  const { price: discountedPrice, campaign } = getDiscountedPrice(product.id, product.category, product.price);

  const cardBg = light ? 'bg-white border border-gray-100 hover:border-gray-200' : 'bg-[#1C2B3A] hover:bg-[#232F3E]';
  const nameColor = light ? 'text-gray-900' : 'text-white';
  const taglineColor = light ? 'text-gray-500' : 'text-white/50';
  const metaColor = light ? 'text-gray-500' : 'text-white/60';
  const imgBg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const catBg = light ? 'bg-white/80 text-gray-600' : 'bg-[#0D1B2A]/80 text-white/70';

  return (
    <div className={`group ${cardBg} rounded-lg overflow-hidden transition-colors cursor-pointer`}>
      <Link to={`/products/${product.id}`} className="block">
        <div className={`relative w-full h-52 overflow-hidden ${imgBg}`}>
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {settings.productBadges && (campaign?.badgeLabel || product.badge) && (
            <span className="absolute top-3 left-3 text-white text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--accent)' }}>
              {campaign?.badgeLabel || product.badge}
            </span>
          )}
          <span className={`absolute top-3 right-3 text-xs px-3 py-1 rounded-full capitalize ${catBg}`}>
            {product.category}
          </span>
        </div>
        <div className="p-5 pb-3">
          <h3 className={`font-bold text-base mb-1 transition-colors group-hover:text-[var(--accent)] ${nameColor}`}>{product.name}</h3>
          <p className={`text-xs mb-3 ${taglineColor}`}>{product.tagline}</p>
          <div className={`flex items-center gap-4 mb-4 text-xs ${metaColor}`}>
            <span className="flex items-center gap-1">
              <i className="ri-flashlight-line" style={{ color: 'var(--accent)' }}></i>
              {product.capacity}Ah
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-plug-line" style={{ color: 'var(--accent)' }}></i>
              {product.voltage}V
            </span>
            {product.cca > 0 && (
              <span className="flex items-center gap-1">
                <i className="ri-speed-line" style={{ color: 'var(--accent)' }}></i>
                {product.cca}CCA
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              {campaign ? (
                <>
                  <span className={`text-xs line-through ${light ? 'text-gray-400' : 'text-white/30'}`}>${product.price.toLocaleString()}</span>
                  <span className="font-black text-lg" style={{ color: 'var(--accent)' }}>${discountedPrice.toLocaleString()}</span>
                </>
              ) : (
                <p className="font-black text-lg" style={{ color: 'var(--accent)' }}>${product.price.toLocaleString()}</p>
              )}
            </div>
            <span className="flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all whitespace-nowrap" style={{ color: 'var(--accent)' }}>
              View Details <i className="ri-arrow-right-line"></i>
            </span>
          </div>
        </div>
      </Link>

      {/* Compare toggle */}
      <div className="px-5 pb-4">
        <button
          onClick={() => toggle(product)}
          disabled={!selected && !canAdd}
          className={`w-full flex items-center justify-center gap-2 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
            canAdd && !selected
              ? light
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-[#0D1B2A] text-white/60 hover:text-white hover:bg-[#0D1B2A]/80'
              : !selected
              ? light
                ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                : 'bg-[#0D1B2A]/40 text-white/20 cursor-not-allowed'
              : 'text-white'
          }`}
          style={selected ? { backgroundColor: 'var(--accent)' } : {}}
        >
          <i className={selected ? 'ri-check-line' : 'ri-bar-chart-grouped-line'}></i>
          {selected ? 'Added to Compare' : canAdd ? 'Add to Compare' : 'Compare Full (3/3)'}
        </button>
      </div>
    </div>
  );
}
